// LAYOUT DE TESTE — RETORNO EM PLANILHA, COM TITULOS E IDENTIFICADORES TORTOS.
//
// Sintetico, `situacao: "teste"`, nunca atribuivel — ver `posicional-retorno.ts`
// para o porque. Este cobre os valores da matriz que a PLANILHA traz e o texto
// delimitado nao:
//
//   planilha-abas · planilha-por-arquivo · celulas-planilha ·
//   titulos-antes-do-cabecalho · decimal-ponto · separador-de-milhar ·
//   serial-planilha · aaaa-mm-dd · cpf-sem-zeros · cpf-com-mascara ·
//   matricula-com-sufixo · sem-parcela-e-total
//
// ELE ACEITA AS DUAS FORMAS, E E' ISSO O PONTO. A mesma informacao chega como
// TRES ABAS de um xlsx (`origem: "planilha"`, `celulas`) ou como TRES ARQUIVOS
// de texto com os mesmos cabecalhos. `forma/tabela.ts` (`linhasDe`) e' o unico
// que enxerga a diferenca; daqui para baixo o adaptador nao sabe de onde as
// celulas vieram. `equivalencia-planilha.test.ts` transforma isso em portao: o
// mesmo conteudo nas duas formas produz pacote IDENTICO e relatorio identico
// exceto `lidos[].arquivo` — que e' o unico campo que fala do continente em vez
// do conteudo (FR-021).
//
// E ELE RECONHECE PELO CONTEUDO. Nao ha nome padrao: cada arquivo (ou aba) e'
// classificado pelo CONJUNTO DE COLUNAS do seu cabecalho, procurado dentro das
// primeiras linhas para pular os titulos do relatorio. `Contrato` e' o que
// distingue o extrato de descontos, `Tipo` e' o que distingue o holerite — sem
// um discriminador as duas abas teriam o mesmo cabecalho minimo e a
// classificacao viraria palpite.
import { montarLinhaDelimitada } from "../../csv-delimitado.js";
import { COLUNAS_CARGA, COLUNAS_CONSIGNADOS, RETORNO_SEPARADOR, SITUACAO_CARGA } from "../../retorno.js";
import { COLUNAS_RUBRICA } from "../../rubrica.js";
import { layoutDoCatalogo } from "../catalogo.js";
import { registrarExemplo } from "../exemplos.js";
import { lerData } from "../forma/data.js";
import { lerNumero } from "../forma/numero.js";
import { acharCabecalho, linhasDe, normalizarNomeColuna } from "../forma/tabela.js";
import { cpfComMascara, cpfCompletado, matriculaSemSufixo } from "../identidade.js";
import { relatorioConversaoVazio } from "../relatorio.js";
import { TESTE_PLANILHA_ABAS, TESTE_PLANILHA_COMPETENCIA } from "./planilha-exemplo.js";
import { ABA_DESCONTOS, ABA_HOLERITE, ABA_SERVIDORES, COLUNAS_TESTE_DESCONTOS, COLUNAS_TESTE_HOLERITE, COLUNAS_TESTE_SERVIDORES, DELIMITACAO_EQUIVALENTE, NUMERO_PONTO, OBRIGATORIAS_DESCONTOS, OBRIGATORIAS_HOLERITE, OBRIGATORIAS_SERVIDORES, TITULOS_ANTES_DO_CABECALHO, } from "./planilha-colunas.js";
const CODIGO = "teste-planilha-retorno";
const PAPEIS = ["servidores", "descontos", "holerite"];
const OBRIGATORIAS = {
    servidores: OBRIGATORIAS_SERVIDORES,
    descontos: OBRIGATORIAS_DESCONTOS,
    holerite: OBRIGATORIAS_HOLERITE,
};
const DECLARADAS = {
    servidores: COLUNAS_TESTE_SERVIDORES.map((c) => c.nome),
    descontos: COLUNAS_TESTE_DESCONTOS.map((c) => c.nome),
    holerite: COLUNAS_TESTE_HOLERITE.map((c) => c.nome),
};
function versao() {
    return layoutDoCatalogo(CODIGO).versao;
}
/** O valor de uma coluna pelo nome, ou `""` quando a coluna nao veio. */
function celula(t, linha, coluna) {
    const i = t.mapa[normalizarNomeColuna(coluna)];
    return i === undefined ? "" : (linha[i] ?? "").trim();
}
/**
 * Classifica um arquivo pelo CONJUNTO DE COLUNAS do cabecalho.
 *
 * `null` quando nenhum papel casa — o arquivo simplesmente nao e' deste
 * layout, e quem decide o que fazer com isso e' `reconhecer`.
 */
function classificar(arquivo) {
    const tabela = linhasDe(arquivo, arquivo.origem === "planilha" ? "celulas" : DELIMITACAO_EQUIVALENTE);
    for (const papel of PAPEIS) {
        const achado = acharCabecalho(tabela.linhas, OBRIGATORIAS[papel], {
            pularAte: TITULOS_ANTES_DO_CABECALHO,
        });
        if (!achado)
            continue;
        const conhecidas = new Set(DECLARADAS[papel].map(normalizarNomeColuna));
        return {
            papel,
            nome: arquivo.nome,
            mapa: achado.mapa,
            registros: tabela.linhas.slice(achado.indice + 1).map((l) => [...l]),
            extras: Object.keys(achado.mapa).filter((n) => !conhecidas.has(n)),
        };
    }
    return null;
}
/** As tres tabelas do envio, por papel. Papel repetido fica com a PRIMEIRA. */
function classificarTodas(arquivos) {
    const achadas = new Map();
    for (const arquivo of arquivos) {
        const t = classificar(arquivo);
        if (t && !achadas.has(t.papel))
            achadas.set(t.papel, t);
    }
    return achadas;
}
const ROTULO = {
    servidores: ABA_SERVIDORES,
    descontos: ABA_DESCONTOS,
    holerite: ABA_HOLERITE,
};
function arredondar(n) {
    return Math.round(n * 100) / 100;
}
function juntar(linhas) {
    return linhas.length === 0 ? "" : `${linhas.join("\r\n")}\r\n`;
}
/** ISO (`aaaa-mm-dd`) → `DDMMAAAA`, que e' o que a carga do padrao escreve. */
function isoParaDDMMAAAA(iso) {
    return `${iso.slice(8, 10)}${iso.slice(5, 7)}${iso.slice(0, 4)}`;
}
export const adaptadorTestePlanilhaRetorno = {
    codigo: CODIGO,
    reconhecer(arquivos) {
        const achadas = classificarTodas(arquivos);
        const encontrados = PAPEIS.filter((p) => achadas.has(p)).map((p) => achadas.get(p).nome);
        const faltando = PAPEIS.filter((p) => !achadas.has(p));
        if (faltando.length > 0) {
            return {
                reconhecido: false,
                layout: CODIGO,
                encontrados,
                faltando: [...faltando],
                motivo: `nenhuma aba ou arquivo do envio tem o cabeçalho de ${faltando.map((p) => `\`${ROTULO[p]}\``).join(" e ")} ` +
                    `nas primeiras ${TITULOS_ANTES_DO_CABECALHO + 1} linhas — este layout é reconhecido pelas colunas, não pelo nome`,
            };
        }
        return { reconhecido: true, layout: CODIGO, encontrados, faltando: [] };
    },
    traduzir(arquivos, ctx) {
        const achadas = classificarTodas(arquivos);
        const servidores = achadas.get("servidores");
        const descontos = achadas.get("descontos");
        const holerite = achadas.get("holerite");
        // Pre-condicao, nao conteudo: a borda chama `reconhecer` antes.
        if (!servidores || !descontos || !holerite) {
            throw new Error("teste-planilha-retorno: envio sem servidores, sem descontos ou sem holerite");
        }
        const rel = relatorioConversaoVazio({ codigo: CODIGO, versao: versao() }, ctx.competencia);
        // `lidos[].arquivo` e' o NOME (aba ou arquivo solto) — o unico campo do
        // relatorio que muda entre as duas formas de empacotamento. Tudo o mais
        // fala do conteudo, e por isso e' igual nas duas.
        rel.lidos = [
            { arquivo: servidores.nome, linhas: servidores.registros.length },
            { arquivo: descontos.nome, linhas: descontos.registros.length },
            { arquivo: holerite.nome, linhas: holerite.registros.length },
        ];
        for (const t of [servidores, descontos, holerite]) {
            if (t.extras.length > 0) {
                rel.avisos.push(`colunas não declaradas em ${ROTULO[t.papel]}, ignoradas: ${t.extras.join(", ")}`);
            }
        }
        // ---- Pre-condicao do PAR: a competencia do arquivo e' a da folha.
        const competencia = competenciaDeclarada(servidores);
        if (competencia === null) {
            throw new Error("teste-planilha-retorno: a coluna `MesReferencia` não traz uma data AAAA-MM-DD legível");
        }
        if (competencia !== ctx.competencia) {
            throw new Error(`teste-planilha-retorno: o envio é da competência ${competencia} e esta folha é ${ctx.competencia}`);
        }
        const carga = montarCarga(servidores, ctx, rel);
        const consignados = montarConsignados(descontos, rel);
        const rubricas = montarRubricas(holerite, ctx, rel);
        rel.gerados = [
            { arquivo: "carga", linhas: carga.linhas.length },
            { arquivo: "consignados", linhas: consignados.length },
            { arquivo: "rubrica", linhas: rubricas.length },
        ];
        if (carga.cpfsCompletados > 0) {
            rel.normalizados.push({
                campo: "cpf",
                regra: "completado com zeros à esquerda até 11 dígitos e conferido pelo dígito verificador",
                quantidade: carga.cpfsCompletados,
            });
        }
        if (carga.cpfsSemMascara > 0) {
            rel.normalizados.push({
                campo: "cpf",
                regra: "máscara (pontos e traço) removida",
                quantidade: carga.cpfsSemMascara,
            });
        }
        if (carga.sufixosRemovidos > 0) {
            rel.normalizados.push({
                campo: "matricula",
                regra: "sufixo de vínculo removido — a matrícula é a chave da folha, e o sufixo faria a mesma pessoa aparecer duas vezes",
                quantidade: carga.sufixosRemovidos,
            });
        }
        rel.naoInformaveis.push({
            campo: "parcelaAtual/parcelasTotal",
            motivo: "o extrato de descontos não traz o cronograma da parcela",
            linhasAfetadas: consignados.length,
        });
        rel.totais.origem = {
            servidores_salario: arredondar(carga.totalSalario),
            descontos_valor: arredondar(consignados.reduce((s, c) => s + c.valor, 0)),
            holerite_valor: arredondar(rubricas.reduce((s, r) => s + r.valor, 0)),
        };
        rel.totais.gerado = {
            consignados_valor: arredondar(consignados.reduce((s, c) => s + c.valor, 0)),
            rubrica_valor: arredondar(rubricas.reduce((s, r) => s + r.valor, 0)),
        };
        return {
            pacote: {
                carga: juntar(carga.linhas),
                consignados: juntar(consignados.map((c) => c.linha)),
                rubricas: juntar(rubricas.map((r) => r.linha)),
            },
            relatorio: rel,
        };
    },
};
// ============================================================ partes
/**
 * A competencia do envio: a primeira `MesReferencia` legivel da aba de
 * servidores, reduzida a AAAAMM.
 *
 * E' uma DATA no arquivo (`2026-09-01`) e uma competencia na Atlas. Ler a
 * data e derivar o mes, em vez de aceitar `AAAAMM` direto, e' o que faz este
 * layout exercitar `aaaa-mm-dd` — e e' o que o relatorio real do ente traz.
 */
function competenciaDeclarada(servidores) {
    for (const linha of servidores.registros) {
        const iso = lerData(celula(servidores, linha, "MesReferencia"), "aaaa-mm-dd");
        if (iso !== null)
            return `${iso.slice(0, 4)}${iso.slice(5, 7)}`;
    }
    return null;
}
function montarCarga(t, ctx, rel) {
    const linhas = [];
    const vistas = new Set();
    let totalSalario = 0;
    let cpfsCompletados = 0;
    let cpfsSemMascara = 0;
    let sufixosRemovidos = 0;
    t.registros.forEach((registro, i) => {
        const linha = i + 1;
        const bruta = celula(t, registro, "Matricula");
        const { matricula, sufixo } = matriculaSemSufixo(bruta);
        const descartar = (motivo) => rel.descartadas.push({ arquivo: t.papel, linha, matricula, motivo });
        if (matricula === "")
            return descartar("matrícula vazia");
        if (vistas.has(matricula))
            return descartar(`matrícula repetida na aba (era "${bruta}")`);
        const cpfBruto = celula(t, registro, "CPF");
        const cpf = cpfCompletado(cpfBruto);
        if (!cpf)
            return descartar(`CPF "${cpfBruto}" não confere no dígito verificador depois de completado`);
        if (cpf.completado)
            cpfsCompletados += 1;
        if (cpfComMascara(cpfBruto))
            cpfsSemMascara += 1;
        if (sufixo !== "")
            sufixosRemovidos += 1;
        const situacao = celula(t, registro, "Situacao").toUpperCase();
        if (!SITUACAO_CARGA.includes(situacao)) {
            return descartar(`situação "${situacao}" não é um código do padrão Atlas`);
        }
        const salario = lerNumero(celula(t, registro, "Salario"), NUMERO_PONTO);
        if (salario === null)
            return descartar(`salário "${celula(t, registro, "Salario")}" não é um número`);
        // Serial de planilha: dias desde 30/12/1899. A fracao e' a hora do dia, e
        // a data ignora — admissao nao tem hora.
        const admissaoBruta = celula(t, registro, "Admissao");
        const admissao = lerData(admissaoBruta, "serial-planilha");
        if (admissaoBruta !== "" && admissao === null) {
            rel.avisos.push(`matrícula ${matricula}: admissão "${admissaoBruta}" não é um serial de planilha, campo vazio na carga`);
        }
        vistas.add(matricula);
        totalSalario += salario;
        linhas.push(montarLinhaDelimitada(COLUNAS_CARGA, {
            competencia: ctx.competencia,
            matricula,
            cpf: cpf.cpf,
            nome: celula(t, registro, "Nome"),
            situacao,
            vinculo: celula(t, registro, "Vinculo"),
            cargo: celula(t, registro, "Cargo"),
            dataAdmissao: admissao === null ? "" : isoParaDDMMAAAA(admissao),
            salarioLiquido: salario,
            bancoCodigo: celula(t, registro, "Banco"),
            agencia: celula(t, registro, "Agencia"),
            conta: celula(t, registro, "Conta"),
        }, RETORNO_SEPARADOR));
    });
    if (sufixosRemovidos > 0) {
        rel.avisos.push(`${sufixosRemovidos} matrícula(s) vieram com sufixo de vínculo; a carga usa a matrícula sem ele, e o retorno do ente continuará trazendo o sufixo`);
    }
    return { linhas, totalSalario, cpfsCompletados, cpfsSemMascara, sufixosRemovidos };
}
function montarConsignados(t, rel) {
    const saida = [];
    t.registros.forEach((registro, i) => {
        const linha = i + 1;
        const { matricula } = matriculaSemSufixo(celula(t, registro, "Matricula"));
        const verba = celula(t, registro, "Verba");
        const descartar = (motivo) => rel.descartadas.push({ arquivo: t.papel, linha, matricula, motivo });
        if (matricula === "" || verba === "")
            return descartar("matrícula ou verba vazia");
        const bruto = celula(t, registro, "Valor");
        const valor = lerNumero(bruto, NUMERO_PONTO);
        if (valor === null)
            return descartar(`valor "${bruto}" não é um número`);
        if (valor <= 0) {
            return descartar("valor zerado ou negativo — para a Atlas, a linha ausente já significa não descontada");
        }
        saida.push({
            linha: montarLinhaDelimitada(COLUNAS_CONSIGNADOS, {
                matricula,
                codigoVerba: verba,
                descricao: celula(t, registro, "Descricao"),
                tipo: "D",
                valor,
                parcelasRestantes: "",
                referencia: celula(t, registro, "Contrato"),
                // O extrato nao traz cronograma — `naoInformaveis` registra o preco.
                parcelaAtual: "",
                parcelasTotal: "",
            }, RETORNO_SEPARADOR),
            valor,
        });
    });
    return saida;
}
function montarRubricas(t, ctx, rel) {
    const saida = [];
    t.registros.forEach((registro, i) => {
        const linha = i + 1;
        const { matricula } = matriculaSemSufixo(celula(t, registro, "Matricula"));
        const verba = celula(t, registro, "Verba");
        const descartar = (motivo) => rel.descartadas.push({ arquivo: t.papel, linha, matricula, motivo });
        if (matricula === "" || verba === "")
            return descartar("matrícula ou verba vazia");
        const tipo = celula(t, registro, "Tipo").toUpperCase();
        if (tipo !== "P" && tipo !== "D")
            return descartar(`tipo "${tipo}" não é provento (\`P\`) nem desconto (\`D\`)`);
        const bruto = celula(t, registro, "Valor");
        const valor = lerNumero(bruto, NUMERO_PONTO);
        if (valor === null)
            return descartar(`valor "${bruto}" não é um número`);
        saida.push({
            linha: montarLinhaDelimitada(COLUNAS_RUBRICA, {
                competencia: ctx.competencia,
                matricula,
                codigoVerba: verba,
                descricao: celula(t, registro, "Descricao"),
                tipo,
                valor,
            }, RETORNO_SEPARADOR),
            valor,
        });
    });
    return saida;
}
registrarExemplo(CODIGO, () => ({
    descricao: "Uma planilha com as abas Servidores, Descontos e Holerite: duas linhas de título antes do cabeçalho, " +
        "CPF sem zeros e com máscara, matrícula com sufixo, data serial e valor com separador de milhar.",
    competencia: TESTE_PLANILHA_COMPETENCIA,
    arquivos: TESTE_PLANILHA_ABAS.map((a) => ({
        nome: a.aba,
        aba: a.aba,
        celulas: a.celulas.map((l) => [...l]),
    })),
}));
//# sourceMappingURL=planilha-retorno.js.map